import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { History, RefreshCw, CheckCircle2, XCircle, MinusCircle, ChevronLeft, ChevronRight, FileDown } from "lucide-react";
import { toast } from "sonner";

type SchedulerRun = {
  id: string;
  tick_at: string;
  source: string;
  dispatched: boolean;
  job_id: string | null;
  scope: string | null;
  mode: string | null;
  due_at: string | null;
  next_run_at: string | null;
  articles_in_scope: number | null;
  reason: string | null;
  error: string | null;
};

const PAGE_SIZE = 15;

export function KbSchedulerHistoryPanel() {
  const [rows, setRows] = useState<SchedulerRun[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [scope, setScope] = useState<string>("all");
  const [mode, setMode] = useState<string>("all");
  const [dispatched, setDispatched] = useState<string>("all");

  const load = async () => {
    setLoading(true);
    let q = supabase
      .from("kb_ingest_scheduler_runs")
      .select("*", { count: "exact" })
      .order("tick_at", { ascending: false });

    if (scope !== "all") q = q.eq("scope", scope);
    if (mode !== "all") q = q.eq("mode", mode);
    if (dispatched === "yes") q = q.eq("dispatched", true);
    else if (dispatched === "no") q = q.eq("dispatched", false);

    const from = page * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;
    const { data, count } = await q.range(from, to);
    setRows((data ?? []) as SchedulerRun[]);
    setTotal(count ?? 0);
    setLoading(false);
  };

  useEffect(() => {
    load();
    const ch = supabase
      .channel(`kb-scheduler-runs:${Math.random().toString(36).slice(2)}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "kb_ingest_scheduler_runs" }, () => {
        if (page === 0) load();
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, scope, mode, dispatched]);

  const resetAndReload = () => { setPage(0); };
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  const exportFilteredCsv = async () => {
    let q = supabase
      .from("kb_ingest_scheduler_runs")
      .select("id, tick_at, source, dispatched, job_id, scope, mode, due_at, next_run_at, articles_in_scope, reason, error")
      .order("tick_at", { ascending: false })
      .limit(5000);
    if (scope !== "all") q = q.eq("scope", scope);
    if (mode !== "all") q = q.eq("mode", mode);
    if (dispatched === "yes") q = q.eq("dispatched", true);
    else if (dispatched === "no") q = q.eq("dispatched", false);
    const { data, error } = await q;
    if (error) { toast.error(error.message); return; }
    const items = (data ?? []) as Array<Record<string, unknown>>;
    if (items.length === 0) { toast.info("No ticks match filters."); return; }
    // Renamed `id` -> `tick_id` for downstream traceability.
    const rows = items.map((r) => ({ tick_id: r.id, ...r, id: undefined }));
    const headers = ["tick_id","tick_at","source","dispatched","job_id","scope","mode","due_at","next_run_at","articles_in_scope","reason","error"];
    const esc = (v: unknown) => {
      if (v == null) return "";
      const s = typeof v === "string" ? v : typeof v === "boolean" || typeof v === "number" ? String(v) : JSON.stringify(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => esc((r as Record<string, unknown>)[h])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const tag = [scope, mode, dispatched].filter((x) => x && x !== "all").join("-") || "all";
    a.href = url; a.download = `kb-scheduler-history-${tag}-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success(`Exported ${items.length} ticks`);
    // Audit
    try {
      await supabase.functions.invoke("log-admin-action", {
        body: {
          action: "kb_scheduler_history_export",
          target_table: "kb_ingest_scheduler_runs",
          details: {
            filters: { scope, mode, dispatched },
            row_count: items.length,
            exported_at: new Date().toISOString(),
            format: "csv",
          },
        },
      });
    } catch { /* non-blocking */ }
  };


  return (
    <section className="rounded-xl border border-border bg-card p-4 sm:p-5">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <History className="size-5 text-primary" />
          <div>
            <h2 className="font-display text-base font-bold">Scheduler history</h2>
            <p className="font-mono text-xs text-muted-foreground">
              {total} ticks · page {page + 1}/{totalPages}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={exportFilteredCsv} title="Export filtered ticks as CSV">
            <FileDown className="size-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={load}>
            <RefreshCw className={`size-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      <div className="mb-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
        <Select value={scope} onValueChange={(v) => { setScope(v); resetAndReload(); }}>
          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Scope" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All scopes</SelectItem>
            <SelectItem value="scheduled">scheduled</SelectItem>
            <SelectItem value="test">test</SelectItem>
            <SelectItem value="missing">missing</SelectItem>
          </SelectContent>
        </Select>
        <Select value={mode} onValueChange={(v) => { setMode(v); resetAndReload(); }}>
          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Mode" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All modes</SelectItem>
            <SelectItem value="run">run</SelectItem>
            <SelectItem value="dry_run">dry_run</SelectItem>
          </SelectContent>
        </Select>
        <Select value={dispatched} onValueChange={(v) => { setDispatched(v); resetAndReload(); }}>
          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Dispatched" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any dispatch</SelectItem>
            <SelectItem value="yes">Dispatched</SelectItem>
            <SelectItem value="no">Skipped/error</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No scheduler ticks match the filters.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px] font-mono">
            <thead className="text-muted-foreground">
              <tr className="border-b border-border">
                <th className="py-1.5 text-left">tick</th>
                <th className="text-left">src</th>
                <th className="text-left">dispatched</th>
                <th className="text-left">scope/mode</th>
                <th className="text-right">articles</th>
                <th className="text-left">job_id</th>
                <th className="text-left">reason</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-border/50">
                  <td className="py-1.5 pr-2 whitespace-nowrap">{new Date(r.tick_at).toLocaleString()}</td>
                  <td className="pr-2">{r.source}</td>
                  <td className="pr-2">
                    {r.dispatched ? (
                      <span className="inline-flex items-center gap-1 text-emerald-500"><CheckCircle2 className="size-3" /> yes</span>
                    ) : r.error ? (
                      <span className="inline-flex items-center gap-1 text-destructive"><XCircle className="size-3" /> err</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-muted-foreground"><MinusCircle className="size-3" /> skip</span>
                    )}
                  </td>
                  <td className="pr-2">{r.scope ?? "—"}/{r.mode ?? "—"}</td>
                  <td className="pr-2 text-right">{r.articles_in_scope ?? "—"}</td>
                  <td className="pr-2 truncate max-w-[140px]" title={r.job_id ?? ""}>{r.job_id ? r.job_id.slice(0, 8) : "—"}</td>
                  <td className="pr-2 truncate max-w-[260px]" title={r.reason ?? r.error ?? ""}>
                    {r.reason ?? r.error ?? ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-3 flex items-center justify-end gap-2">
        <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
          <ChevronLeft className="size-3.5" /> Prev
        </Button>
        <span className="font-mono text-[11px] text-muted-foreground">
          {page + 1} / {totalPages}
        </span>
        <Button size="sm" variant="outline" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>
          Next <ChevronRight className="size-3.5" />
        </Button>
      </div>
    </section>
  );
}
